import type { BooMapLink, ContainerRow } from "@/lib/api";

// The order of the Boos page's lists and who learns from whom, shared by the
// list view and the diagram's fallback list. Pure, so `node --test` covers it.

// Depth below the org: a Boo comes after every Boo that learns from it.
export function booDepths(links: BooMapLink[]): (id: string) => number {
  const depth = new Map<string, number>();
  const depthOf = (id: string, seen: Set<string> = new Set()): number => {
    if (depth.has(id)) return depth.get(id)!;
    if (seen.has(id)) return 0;
    seen.add(id);
    const users = links.filter((l) => l.sourceId === id).map((l) => l.consumerId);
    const d = users.length ? 1 + Math.max(...users.map((u) => depthOf(u, seen))) : 0;
    depth.set(id, d);
    return d;
  };
  return depthOf;
}

export function orderBoos(boos: ContainerRow[], links: BooMapLink[]): ContainerRow[] {
  const depthOf = booDepths(links);
  return [...boos].sort((a, b) => depthOf(a.id) - depthOf(b.id) || a.name.localeCompare(b.name));
}

// The Boos one Boo learns from and shares with, and the requests waiting on
// either side.
export function booConnections(
  id: string,
  links: BooMapLink[],
): { uses: string[]; usedBy: string[]; waiting: number } {
  return {
    uses: links.filter((l) => l.consumerId === id && l.status === "approved").map((l) => l.sourceId),
    usedBy: links.filter((l) => l.sourceId === id && l.status === "approved").map((l) => l.consumerId),
    waiting: links.filter((l) => (l.consumerId === id || l.sourceId === id) && l.status === "requested").length,
  };
}
